"use client";

import React, { useEffect, useId, useLayoutEffect, useRef } from "react";
import Image from "next/image";
import { BagIcon } from "@/components/Icons";

interface ProductGalleryProps {
  /** Galerij van de gekozen kleur (galleryFor() in ProductDetailClient). */
  images: string[];
  /** Bron van waarheid blijft de pagina: thumbnails, zoomweergave en winkelmand lezen die ook. */
  selectedImage: string;
  onSelect: (url: string) => void;
  /** Tik/klik of Enter op de foto: opent de bestaande zoomweergave. */
  onZoom: (url: string) => void;
  /** Zoomweergave staat open: pijltjestoetsen horen dan alleen bij de zoomweergave. */
  zoomOpen: boolean;
  productName: string;
  /** Wisselt per kleur: andere kleur = andere galerij, de track begint dan weer bij foto 1. */
  resetKey: string;
  /** Statuslabel (sale / nieuw / bestseller / uitgelicht), linksboven in beeld. */
  badge?: React.ReactNode;
}

// Zo lang negeren we scroll-events na een door ons gestarte (smooth) scroll,
// anders zou de tussenliggende foto kort als "gekozen" oplichten in de thumbnails.
const SMOOTH_LOCK_MS = 800;
const INSTANT_LOCK_MS = 100;

// Zoveel slides laden direct (eager). Bijna alle galerijen hebben 1-5 foto's en de
// thumbnails eronder halen dezelfde bestanden toch al op, dus dit kost geen extra
// verkeer. Reden: een lazy slide buiten beeld kan in Safari/iOS nog leeg zijn op het
// moment dat je er naartoe veegt (niet op een echt toestel getest).
const EAGER_SLIDES = 6;

/**
 * Hoofdfoto van de productpagina als scroll-snap-carrousel: de foto's staan
 * naast elkaar in een horizontaal scrollende track, de browser doet het
 * vinger-volgen, de momentum-scroll en het "vastklikken" op de volgende foto.
 * Géén eigen touch-handlers, dus verticaal scrollen van de pagina blijft
 * gewoon werken (en daarom bewust géén touch-action: pan-y op de track).
 */
export default function ProductGallery({
  images, selectedImage, onSelect, onZoom, zoomOpen, productName, resetKey, badge,
}: ProductGalleryProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef(false);
  const lockTimer = useRef(0);
  const frame = useRef(0);
  // Uniek id voor de verborgen hint (aria-describedby); een pagina kan meerdere galerijen hebben.
  const hintId = useId();

  // Laatste props voor de rAF-callback (voorkomt een verouderde closure).
  const latest = useRef({ images, selectedImage, onSelect });
  useLayoutEffect(() => { latest.current = { images, selectedImage, onSelect }; });

  const count = images.length;
  const imagesKey = images.join("|");
  const activeIndex = Math.max(0, images.indexOf(selectedImage));

  function scrollToIndex(index: number, smooth: boolean) {
    const el = trackRef.current;
    if (!el) return;
    lockRef.current = true;
    window.clearTimeout(lockTimer.current);
    lockTimer.current = window.setTimeout(() => { lockRef.current = false; }, smooth ? SMOOTH_LOCK_MS : INSTANT_LOCK_MS);
    // Expliciet behavior meegeven: <html> heeft scroll-behavior:smooth.
    el.scrollTo({ left: index * el.clientWidth, behavior: smooth ? "smooth" : "instant" });
  }

  // state → scroll: thumbnail-klik, pijltjestoets of bladeren in de zoomweergave.
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || count < 2) return;
    const target = images.indexOf(selectedImage);
    if (target < 0 || !el.clientWidth) return;
    if (Math.round(el.scrollLeft / el.clientWidth) === target) return; // staat er al (bv. na een swipe)
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    scrollToIndex(target, !reduce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, imagesKey]);

  // Bij draaien van het toestel / andere breedte: opnieuw op de juiste foto uitlijnen.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      scrollToIndex(Math.max(0, latest.current.images.indexOf(latest.current.selectedImage)), false);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [resetKey, count]);

  useEffect(() => () => {
    cancelAnimationFrame(frame.current);
    window.clearTimeout(lockTimer.current);
  }, []);

  // scroll → state: de bezoeker veegt zelf.
  function handleScroll() {
    if (lockRef.current) return;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = trackRef.current;
      if (!el || !el.clientWidth) return;
      const { images: list, selectedImage: current, onSelect: select } = latest.current;
      const url = list[Math.min(list.length - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)))];
      if (url && url !== current) select(url);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (zoomOpen) return;
    if (e.key === "ArrowRight" && activeIndex < count - 1) { e.preventDefault(); onSelect(images[activeIndex + 1]); }
    else if (e.key === "ArrowLeft" && activeIndex > 0) { e.preventDefault(); onSelect(images[activeIndex - 1]); }
    else if (e.key === "Home" && count > 1) { e.preventDefault(); onSelect(images[0]); }
    else if (e.key === "End" && count > 1) { e.preventDefault(); onSelect(images[count - 1]); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onZoom(images[activeIndex]); }
  }

  // Geen foto's voor deze kleur: zelfde placeholder als voorheen.
  if (count === 0) {
    return (
      <div className="pdp-gallery pdp-gallery--empty">
        <BagIcon size="3.5rem" strokeWidth={1.2} style={{ color: "var(--color-gray-300)" }} />
        {badge && <div className="pdp-gallery-overlay">{badge}</div>}
      </div>
    );
  }

  return (
    <div className="pdp-gallery">
      <div
        key={resetKey}
        ref={trackRef}
        className="pdp-gallery-track"
        role="group"
        aria-roledescription="carousel"
        aria-label={`Foto's van ${productName}`}
        // Dit element opent bij Enter/Spatie/klik de zoomweergave (voorheen role="button"). Dat zegt de rol
        // "carrousel" niet, dus een korte beschrijving; geen role="button" op een container met slides.
        aria-describedby={hintId}
        tabIndex={0}
        title="Klik om te vergroten"
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        onClick={() => onZoom(images[activeIndex])}
      >
        {images.map((url, i) => (
          <div
            // Index erbij: dezelfde URL kan (zeldzaam) twee keer in de lijst staan.
            key={`${i}-${url}`}
            className="pdp-gallery-slide"
            role="group"
            aria-roledescription="slide"
            aria-label={`Foto ${i + 1} van ${count}`}
          >
            <Image
              src={url}
              alt={i === 0 ? productName : `${productName} afbeelding ${i + 1}`}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              loading={i < EAGER_SLIDES ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
              draggable={false}
              style={{ objectFit: "cover" }}
            />
          </div>
        ))}
      </div>

      {/* Overlays: pointer-events:none, anders blokkeren ze het vegen over de foto. */}
      {badge && <div className="pdp-gallery-overlay">{badge}</div>}
      {count > 1 && (
        <div className="pdp-gallery-dots" aria-hidden="true">
          {images.map((url, i) => (
            <span key={`${i}-${url}`} className={`pdp-gallery-dot${i === activeIndex ? " is-active" : ""}`} />
          ))}
        </div>
      )}
      {/* hidden: alleen als beschrijving van de carrousel (aria-describedby leest ook verborgen tekst), niet als losse tekst in de leesvolgorde. */}
      <span id={hintId} hidden>
        {count > 1
          ? "Druk op Enter om de foto te vergroten. Met de pijltjestoetsen blader je door de foto's."
          : "Druk op Enter om de foto te vergroten."}
      </span>
      <div className="pdp-gallery-hint" aria-hidden="true">
        <span className="pdp-hint-fine">Klik voor zoom</span>
        <span className="pdp-hint-coarse">Tik om te vergroten</span>
      </div>
      {count > 1 && (
        <span className="sr-only" aria-live="polite">{`Foto ${activeIndex + 1} van ${count}`}</span>
      )}
    </div>
  );
}
